import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { designSystem } from '../../styles/design-system';
import { channelSyncManager, SyncStatus } from '../../utils/channelSync';
import Button from '../ui/Button';

// ===== STYLED COMPONENTS =====

const SyncContainer = styled(motion.div)`
  background: ${designSystem.colors.background.tertiary};
  border-radius: ${designSystem.borderRadius.xl};
  padding: ${designSystem.spacing.xl};
  border: 1px solid ${designSystem.colors.border.light};
  margin-bottom: ${designSystem.spacing.xl};
`;

const SyncHeader = styled.div`
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: ${designSystem.spacing.lg};
  gap: ${designSystem.spacing.md};
  
  h3 {
    color: ${designSystem.colors.text.primary};
    font-size: ${designSystem.typography.fontSize.xl};
    font-weight: ${designSystem.typography.fontWeight.semibold};
    margin: 0;
    flex: 1;
  }
`;

const ChannelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${designSystem.spacing.lg};
  margin-bottom: ${designSystem.spacing.xl};
`;

const ChannelCard = styled.div<{ status: 'success' | 'error' | 'pending' }>`
  background: ${designSystem.colors.background.secondary};
  border-radius: ${designSystem.borderRadius.lg};
  padding: ${designSystem.spacing.lg};
  border-left: 4px solid ${props => {
    switch (props.status) {
      case 'success': return designSystem.colors.status.success;
      case 'error': return designSystem.colors.status.error;
      case 'pending': return designSystem.colors.status.warning;
      default: return designSystem.colors.border.light;
    }
  }};
  transition: all ${designSystem.transitions.normal};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${designSystem.shadows.hover};
  }
`;

const ChannelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${designSystem.spacing.md};
`;

const ChannelTitle = styled.h4`
  color: ${designSystem.colors.text.primary};
  font-size: ${designSystem.typography.fontSize.lg};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${designSystem.spacing.sm};
`;

const StatusBadge = styled.span<{ status: 'success' | 'error' | 'pending' }>`
  padding: ${designSystem.spacing.xs} ${designSystem.spacing.sm};
  border-radius: ${designSystem.borderRadius.md};
  font-size: ${designSystem.typography.fontSize.sm};
  font-weight: ${designSystem.typography.fontWeight.medium};
  color: white;
  background: ${props => {
    switch (props.status) {
      case 'success': return designSystem.colors.status.success;
      case 'error': return designSystem.colors.status.error;
      case 'pending': return designSystem.colors.status.warning;
      default: return designSystem.colors.secondary.main;
    }
  }};
`;

const SyncDetails = styled.div`
  color: ${designSystem.colors.text.secondary};
  font-size: ${designSystem.typography.fontSize.sm};
  margin-bottom: ${designSystem.spacing.md};
  
  p {
    margin: ${designSystem.spacing.xs} 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${designSystem.spacing.sm};
  margin-top: ${designSystem.spacing.md};
`;

const SyncAllContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${designSystem.spacing.md};
  padding-top: ${designSystem.spacing.lg};
  border-top: 1px solid ${designSystem.colors.border.light};
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid ${designSystem.colors.border.light};
  border-top: 2px solid ${designSystem.colors.primary.main};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: ${designSystem.spacing.sm};

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// ===== COMPONENT =====

interface ChannelSyncPanelProps {
  className?: string;
}

const ChannelSyncPanel: React.FC<ChannelSyncPanelProps> = ({ className }) => {
  const [syncStatuses, setSyncStatuses] = useState<SyncStatus[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Recupera stato sincronizzazione all'avvio
  useEffect(() => {
    loadSyncStatus();
  }, []);

  const loadSyncStatus = async () => {
    try {
      // Simula caricamento stato sync dal database
      const mockStatuses: SyncStatus[] = [
        {
          platform: 'airbnb',
          status: 'success',
          lastSync: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 ore fa
          message: 'Ultima sincronizzazione completata con successo'
        },
        {
          platform: 'avaibook',
          status: 'success',
          lastSync: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 minuti fa
          message: 'Sincronizzazione XML attiva'
        }
      ];
      
      setSyncStatuses(mockStatuses);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Errore caricamento stato sync:', error);
    }
  };

  const handleSyncAll = async () => {
    setIsSyncing(true);
    try {
      const results = await channelSyncManager.syncAllChannels();
      setSyncStatuses(results);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Errore sincronizzazione:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSyncChannel = async (platform: 'airbnb' | 'avaibook') => {
    setIsSyncing(true);
    try {
      // Sincronizza singolo canale
      const results = await channelSyncManager.syncAllChannels();
      const channelResult = results.find(r => r.platform === platform);
      
      if (channelResult) {
        setSyncStatuses(prev => 
          prev.map(status => 
            status.platform === platform ? channelResult : status
          )
        );
      }
      
      setLastUpdate(new Date());
    } catch (error) {
      console.error(`Errore sincronizzazione ${platform}:`, error);
    } finally {
      setIsSyncing(false);
    }
  };

  const getChannelIcon = (platform: string) => {
    switch (platform) {
      case 'airbnb': return '🏠';
      case 'avaibook': return '📊';
      default: return '🔗';
    }
  };

  const getChannelName = (platform: string) => {
    switch (platform) {
      case 'airbnb': return 'Airbnb';
      case 'avaibook': return 'AvaiBook';
      default: return platform;
    }
  };

  const formatLastSync = (lastSync: string) => {
    const date = new Date(lastSync);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffMins < 60) {
      return `${diffMins} minuti fa`;
    } else if (diffHours < 24) {
      return `${diffHours} ore fa`;
    } else {
      return date.toLocaleDateString('it-IT') + ' ' + date.toLocaleTimeString('it-IT');
    }
  };

  return (
    <SyncContainer
      className={className}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <SyncHeader>
        <h3>🔄 Sincronizzazione Canali</h3>
        <div style={{ color: designSystem.colors.text.secondary, fontSize: designSystem.typography.fontSize.sm }}>
          Ultimo aggiornamento: {lastUpdate.toLocaleTimeString('it-IT')}
        </div>
      </SyncHeader>

      <ChannelGrid>
        {syncStatuses.map((status) => (
          <ChannelCard key={status.platform} status={status.status}>
            <ChannelHeader>
              <ChannelTitle>
                {getChannelIcon(status.platform)}
                {getChannelName(status.platform)}
              </ChannelTitle>
              <StatusBadge status={status.status}>
                {status.status === 'success' && '✅ Sincronizzato'}
                {status.status === 'error' && '❌ Errore'}
                {status.status === 'pending' && '⏳ In corso'}
              </StatusBadge>
            </ChannelHeader>
            
            <SyncDetails>
              <p><strong>Ultima sincronizzazione:</strong> {formatLastSync(status.lastSync)}</p>
              {status.message && <p><strong>Stato:</strong> {status.message}</p>}
            </SyncDetails>
            
            <ActionButtons>
              <Button
                size="small"
                variant="outline"
                onClick={() => handleSyncChannel(status.platform)}
                disabled={isSyncing}
              >
                {isSyncing && <LoadingSpinner />}
                Sincronizza
              </Button>
              
              <Button
                size="small"
                variant="text"
                onClick={loadSyncStatus}
              >
                📊 Dettagli
              </Button>
            </ActionButtons>
          </ChannelCard>
        ))}
      </ChannelGrid>

      <SyncAllContainer>
        <Button
          onClick={handleSyncAll}
          disabled={isSyncing}
          size="large"
        >
          {isSyncing && <LoadingSpinner />}
          {isSyncing ? 'Sincronizzazione in corso...' : '🔄 Sincronizza Tutti i Canali'}
        </Button>
        
        <Button
          variant="outline"
          onClick={loadSyncStatus}
          size="large"
        >
          🔍 Aggiorna Stato
        </Button>
      </SyncAllContainer>
    </SyncContainer>
  );
};

export default ChannelSyncPanel; 